import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { needsRescale } from '../signaturePlacementShared.ts';
import { expectedAspectRatio, pdfToBrowser } from './placement.utils.ts';
import { findOverlappingKeys, samePosition, settlePlacements } from './placementCollision.utils.ts';
import type {
	CanvasPageInfo,
	CanvasPlacement,
	PageDimensionsPt,
	PageRefs,
	PdfBox,
	ScrollHostRef,
	UsePlacementCanvasArgs,
	UseSettlePlacementsArgs,
} from './PlacementCanvas.types.ts';

const NO_FIXED_PLACEMENTS: CanvasPlacement[] = [];

/**
 * Track the most-centred page in the scroll viewport and notify on change. Returns
 * the scroll-host ref plus `emitVisiblePage` to call once after pages mount
 * (the scroll listener handles changes thereafter).
 */
export const useVisiblePage = (
	pageRefs: PageRefs,
	onVisiblePageChange?: (pageNum: number) => void
): { scrollHostRef: ScrollHostRef; emitVisiblePage: () => void } => {
	const scrollHostRef = useRef<HTMLDivElement | null>(null);
	const lastEmittedRef = useRef<number | null>(null);
	// Stable ref so the scroll listener needn't re-bind on every parent re-render.
	const callbackRef = useRef(onVisiblePageChange);
	callbackRef.current = onVisiblePageChange;

	const emitVisiblePage = useCallback(() => {
		const cb = callbackRef.current;
		if (!cb) {
			return;
		}
		const scrollHost = scrollHostRef.current;
		if (!scrollHost) {
			return;
		}
		const hostRect = scrollHost.getBoundingClientRect();
		const hostCentreY = hostRect.top + scrollHost.clientHeight / 2;
		let bestPage: number | null = null;
		let bestDist = Number.POSITIVE_INFINITY;
		for (const pageNumStr of Object.keys(pageRefs.current)) {
			const pageNum = Number(pageNumStr);
			const el = pageRefs.current[pageNum];
			if (!el) {
				continue;
			}
			const r = el.getBoundingClientRect();
			const pageCentreY = r.top + r.height / 2;
			const dist = Math.abs(pageCentreY - hostCentreY);
			if (dist < bestDist) {
				bestDist = dist;
				bestPage = pageNum;
			}
		}
		if (bestPage !== null && bestPage !== lastEmittedRef.current) {
			lastEmittedRef.current = bestPage;
			cb(bestPage);
		}
	}, [pageRefs]);

	useEffect(() => {
		const scrollHost = scrollHostRef.current;
		if (!scrollHost) {
			return;
		}
		const handler = () => emitVisiblePage();
		scrollHost.addEventListener('scroll', handler, { passive: true });
		return () => {
			scrollHost.removeEventListener('scroll', handler);
		};
	}, [emitVisiblePage]);

	return { scrollHostRef, emitVisiblePage };
};

/**
 * Smooth-scroll the selected stamp to viewport centre on selection change (once
 * per key). No-op until the target page has rendered (needs rect + dims).
 */
export const useScrollToSelected = (args: {
	selectedSlotKey: string | null;
	placements: Map<string, CanvasPlacement>;
	pageDims: Map<number, PageDimensionsPt>;
	scale: number;
	pageRefs: PageRefs;
	scrollHostRef: ScrollHostRef;
}): void => {
	const { selectedSlotKey, placements, pageDims, scale, pageRefs, scrollHostRef } = args;
	const lastScrolledKeyRef = useRef<string | null>(null);

	useEffect(() => {
		if (!selectedSlotKey) {
			lastScrolledKeyRef.current = null;
			return;
		}
		if (lastScrolledKeyRef.current === selectedSlotKey) {
			return;
		}
		const placement = placements.get(selectedSlotKey);
		if (!placement) {
			return;
		}
		const target = pageRefs.current[placement.pageNum];
		const dims = pageDims.get(placement.pageNum);
		const scrollHost = scrollHostRef.current;
		if (!target || !scrollHost || !dims) {
			return;
		}
		// Centre on the stamp, not the page; derive page-top via
		// getBoundingClientRect deltas since offsetParent may not be the host.
		const pageRect = target.getBoundingClientRect();
		const hostRect = scrollHost.getBoundingClientRect();
		const pageTopInScrollSpace = pageRect.top - hostRect.top + scrollHost.scrollTop;
		const browserBox = pdfToBrowser(placement.pdfBox, dims.heightPt, scale);
		const stampCentreY = pageTopInScrollSpace + browserBox.top + browserBox.height / 2;
		scrollHost.scrollTo({
			top: stampCentreY - scrollHost.clientHeight / 2,
			behavior: 'smooth',
		});
		lastScrolledKeyRef.current = selectedSlotKey;
	}, [selectedSlotKey, placements, pageDims, scale, pageRefs, scrollHostRef]);
};

/**
 * Pull every box onto its page and move overlapping ones apart, without a drag.
 *
 * Covers what no gesture does: a box grown by a ratio rescale (a witness slot, or any slot after
 * a "Show details" flip) grows upward from a fixed bottom edge, so it can overshoot the page —
 * which `fitPlacement` drops bottom-left at signing (DC-1118) — or run into its neighbour; and a
 * template saved before overlaps were prevented can load with them. A box just added yields
 * rather than shoving an existing one.
 *
 * A layout effect so a freshly placed box never paints at its default spot first.
 */
export const useSettlePlacements = ({
	placements,
	pageDims,
	fixedPlacements,
	ratioFor,
	onPlacementChange,
	onBoxesShifted,
}: UseSettlePlacementsArgs): void => {
	// Refs so a new inline callback each render can't re-trigger the effect.
	const callbacksRef = useRef({ ratioFor, onPlacementChange, onBoxesShifted });
	callbacksRef.current = { ratioFor, onPlacementChange, onBoxesShifted };
	const previousKeysRef = useRef(new Set<string>());
	// Last write asked for per key, so a write the owner did not apply is not asked for forever.
	const requestedRef = useRef(new Map<string, { from: PdfBox; to: PdfBox }>());

	useLayoutEffect(() => {
		const callbacks = callbacksRef.current;
		// The state hooks' rescale effects own a mis-ratio box's next write; moving it now would
		// race them, and whichever lands second would undo the other (DC-1163).
		const awaitingRescale = Array.from(placements.entries()).some(([key, placement]) => {
			const ratio = callbacks.ratioFor(key);
			return ratio !== null && needsRescale(placement, ratio);
		});
		if (awaitingRescale) {
			return;
		}

		const keys = new Set(placements.keys());
		const previousKeys = previousKeysRef.current;
		// A gesture adds one box at a time; several keys arriving together are a load, whose
		// overlaps were saved that way and are worth reporting.
		const addedKeys = Array.from(keys).filter((key) => !previousKeys.has(key));
		const yieldingKeys = new Set(addedKeys.length === 1 ? addedKeys : []);
		previousKeysRef.current = keys;

		const { moves, shiftedKeys } = settlePlacements({
			placements,
			pageDims,
			fixedPlacements,
			yieldingKeys,
		});

		let shiftedCount = 0;
		moves.forEach((move, key) => {
			const current = placements.get(key)?.pdfBox;
			const requested = requestedRef.current.get(key);
			if (
				!current ||
				(requested &&
					samePosition(requested.from, current) &&
					samePosition(requested.to, move.pdfBox))
			) {
				return;
			}
			requestedRef.current.set(key, { from: current, to: move.pdfBox });
			callbacks.onPlacementChange(key, move.pageNum, move.pdfBox);
			if (shiftedKeys.has(key) && !yieldingKeys.has(key)) {
				shiftedCount += 1;
			}
		});
		requestedRef.current.forEach((_, key) => {
			if (!moves.has(key)) {
				requestedRef.current.delete(key);
			}
		});
		if (shiftedCount > 0) {
			callbacks.onBoxesShifted(shiftedCount);
		}
	}, [placements, pageDims, fixedPlacements]);
};

/**
 * Everything the placement overlay needs that is not the PDF itself.
 *
 * Split out when the two surfaces started sharing one `PdfPageCanvas`: the page sizes and refs
 * have to be owned OUTSIDE it, because the scroll-to-selected hook and the drag-across-pages
 * resolver both read them from outside the render.
 */
export const usePlacementCanvas = ({
	placements,
	selectedSlotKey,
	scale,
	slotByKey,
	showMetadata,
	fixedPlacements = NO_FIXED_PLACEMENTS,
	onVisiblePageChange,
	onPlacementChange,
}: UsePlacementCanvasArgs) => {
	const tMipManage = useTranslationMipManage();
	const [pageDims, setPageDims] = useState<Map<number, PageDimensionsPt>>(new Map());
	const pageRefs = useRef<Record<number, HTMLDivElement | null>>({});

	const { scrollHostRef, emitVisiblePage } = useVisiblePage(pageRefs, onVisiblePageChange);
	useScrollToSelected({ selectedSlotKey, placements, pageDims, scale, pageRefs, scrollHostRef });
	// Called here rather than in one canvas component: both the placement editor and the merged
	// PDF editor go through this hook.
	useSettlePlacements({
		placements,
		pageDims,
		fixedPlacements,
		ratioFor: (key) => {
			const meta = slotByKey.get(key);
			return meta ? expectedAspectRatio(showMetadata, meta.isWitness) : null;
		},
		onPlacementChange,
		onBoxesShifted: (count) =>
			showToastV2({
				// One toast per burst: pages render one by one, each settling its own boxes.
				id: 'placement-overlap-resolved',
				state: 'warning',
				// Two literal calls, not one with a ternary key, so the key lint can see both.
				title:
					count === 1
						? tMipManage('master_documents.placement.overlap_resolved_one', { count })
						: tMipManage('master_documents.placement.overlap_resolved_other', {
								count,
							}),
			}),
	});

	const overlappingKeys = useMemo(
		() => findOverlappingKeys(placements, fixedPlacements),
		[placements, fixedPlacements]
	);

	const obstaclesOnPage = useCallback(
		(pageNum: number, excludeKey?: string): PdfBox[] => [
			...fixedPlacements
				.filter((placement) => placement.pageNum === pageNum)
				.map((placement) => placement.pdfBox),
			...Array.from(placements.entries())
				.filter(([key, placement]) => key !== excludeKey && placement.pageNum === pageNum)
				.map(([, placement]) => placement.pdfBox),
		],
		[placements, fixedPlacements]
	);

	const placementsByPage = useMemo(() => {
		const grouped = new Map<number, Array<{ key: string; placement: CanvasPlacement }>>();
		placements.forEach((placement, key) => {
			const list = grouped.get(placement.pageNum) ?? [];
			list.push({ key, placement });
			grouped.set(placement.pageNum, list);
		});
		return grouped;
	}, [placements]);

	const onPageRenderSuccess = useCallback(
		(pageNum: number, page: { originalWidth: number; originalHeight: number }) => {
			setPageDims((prev) => {
				const existing = prev.get(pageNum);
				if (
					existing &&
					existing.widthPt === page.originalWidth &&
					existing.heightPt === page.originalHeight
				) {
					return prev;
				}
				const next = new Map(prev);
				next.set(pageNum, { widthPt: page.originalWidth, heightPt: page.originalHeight });
				return next;
			});
			// Emit an initial visible-page signal now the page is in the DOM;
			// no-op once the scroll listener takes over.
			emitVisiblePage();
		},
		[emitVisiblePage]
	);

	// Live page rects + dims, so the pure drop resolver can decide which page a dragged stamp
	// landed on (supports drag-across-pages).
	const collectPages = useCallback((): CanvasPageInfo[] => {
		const pages: CanvasPageInfo[] = [];
		for (const pageNumKey of Object.keys(pageRefs.current)) {
			const pageNum = Number(pageNumKey);
			const element = pageRefs.current[pageNum];
			const dims = pageDims.get(pageNum);
			if (!element || !dims) {
				continue;
			}
			const rect = element.getBoundingClientRect();
			pages.push({
				pageNum,
				rect: { left: rect.left, right: rect.right, top: rect.top, bottom: rect.bottom },
				dims,
			});
		}
		return pages;
	}, [pageDims]);

	return {
		pageDims,
		placementsByPage,
		overlappingKeys,
		obstaclesOnPage,
		onPageRenderSuccess,
		scrollHostRef,
		pageRefs,
		collectPages,
	};
};
